// ═══════════════════════════════════════
// CURSOR-SYNC — Synchro curseur éditeur ↔ rendu abcjs
// ═══════════════════════════════════════
//
// abcjs donne pour chaque élément gravé (note, silence, barre…) sa position
// dans la chaîne passée à ABCJS.renderAbc : abcelem.startChar / endChar.
//
//   Rendu → éditeur : le clickListener reçoit l'abcelem cliqué ; on convertit
//                     startChar / endChar en (voix, ligne, colonne) et on y
//                     place la sélection (comme ABCJS.Editor.highlight).
//   Éditeur → rendu : on convertit la sélection de l'éditeur en offsets et on
//                     appelle tune.engraver.rangeHighlight(start, end)
//                     (comme ABCJS.Editor.updateSelection).

import { getVoiceSelection, selectInVoice } from './editor.js';

let _visualObj   = null;
let _lineStarts  = [];   // offset de début de chaque ligne de l'ABC rendu
let _lineOrigins = [];   // origine de chaque ligne rendue : { voice, line, col0 } | null

/**
 * À appeler après chaque ABCJS.renderAbc().
 * @param {string}   preAbc       — ABC produit par getAbcForRenderWithMap()
 * @param {Array}    preLineMap   — lineMap produit par getAbcForRenderWithMap()
 * @param {string}   renderedAbc  — ABC réellement passé à ABCJS.renderAbc()
 * @param {object}   visualObj    — résultat de ABCJS.renderAbc()[0]
 * @param {string}   insertedLine — ligne que le renderer peut insérer entre
 *                                  preAbc et renderedAbc (transpose breton)
 */
export function setRenderSourceMap(preAbc, preLineMap, renderedAbc, visualObj, insertedLine) {
    _visualObj = visualObj;

    const pre = preAbc.split('\n');
    const fin = renderedAbc.split('\n');

    // Le renderer n'ajoute que des lignes entières (insertedLine) ou remplace
    // une ligne d'entête : on réaligne ligne à ligne.
    _lineOrigins = new Array(fin.length).fill(null);
    let j = 0;
    for (let p = 0; p < pre.length && j < fin.length; p++) {
        while (j < fin.length && fin[j] !== pre[p] && fin[j] === insertedLine) j++;
        if (j >= fin.length) break;
        _lineOrigins[j] = preLineMap[p] ?? null;
        j++;
    }

    _lineStarts = [];
    let off = 0;
    for (const l of fin) {
        _lineStarts.push(off);
        off += l.length + 1;
    }
}

// ── Conversions offset ↔ éditeur ─────────────────────────────────────────────

function _offsetToEditor(off) {
    if (off == null || _lineStarts.length === 0) return null;
    let i = _lineStarts.length - 1;
    while (i > 0 && _lineStarts[i] > off) i--;
    const o = _lineOrigins[i];
    if (!o) return null;
    return { voice: o.voice, line: o.line, col: off - _lineStarts[i] + o.col0 };
}

function _editorToOffset(voice, line, col) {
    for (let i = 0; i < _lineOrigins.length; i++) {
        const o = _lineOrigins[i];
        if (o && o.voice === voice && o.line === line) {
            return _lineStarts[i] + Math.max(0, col - o.col0);
        }
    }
    return null;
}

// ── Éditeur → rendu ──────────────────────────────────────────────────────────

function _highlightRange(start, end) {
    const engraver = _visualObj?.engraver;
    if (!engraver || typeof engraver.rangeHighlight !== 'function') return;

    // Curseur sans sélection : rangeHighlight(c, c) ne surligne que l'élément
    // qui se termine en c. Si le caractère sous le curseur appartient à un
    // élément (getElementFromChar), on surligne plutôt celui-là.
    if (start === end && start >= 0 && _visualObj.getElementFromChar(start)) end = start + 1;

    engraver.rangeHighlight(start, end);
}

/**
 * Surligne dans le rendu l'élément sous le curseur de l'éditeur.
 * Ne fait rien si la sélection n'est pas dans une voix.
 */
export function syncRenderFromCursor() {
    const pos = getVoiceSelection();
    if (!pos) return;

    const start = _editorToOffset(pos.voice, pos.lineIdx, pos.col);
    const end   = _editorToOffset(pos.voice, pos.endLineIdx, pos.endCol);

    // Voix masquée ou ligne absente du rendu → on efface le surlignage
    if (start == null) { _highlightRange(-1, -1); return; }
    _highlightRange(start, end ?? start);
}

// ── Rendu → éditeur ──────────────────────────────────────────────────────────

function _isEditorOpen() {
    const panel = document.getElementById('editor-panel');
    if (!panel) return false;
    // Même logique que initResizer() dans editor/main.js
    if (window.innerWidth <= 768) {
        return document.getElementById('editor-resizer')?.classList.contains('editor-open') ?? false;
    }
    return panel.offsetWidth >= 30;
}

/**
 * À appeler depuis le clickListener abcjs.
 */
export function onRenderClick(abcelem) {
    if (abcelem?.startChar == null || abcelem.endChar == null) return;
    if (!_isEditorOpen()) return;

    const a = _offsetToEditor(abcelem.startChar);
    if (!a) return;
    const b = _offsetToEditor(abcelem.endChar);
    const end = (b && b.voice === a.voice) ? b : a;

    // abcjs termine son propre traitement du clic (focus de l'élément SVG)
    // après l'appel du listener : on place la sélection juste après.
    setTimeout(() => selectInVoice(a.voice, a.line, a.col, end.line, end.col), 0);
}

// ── Init ─────────────────────────────────────────────────────────────────────

export function initCursorSync() {
    document.addEventListener('selectionchange', syncRenderFromCursor);
}
